import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, LoaderCircleIcon, SendHorizontalIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { NisoloSubmission, SizeTier } from '../hooks/useNisoloSubmissions';
import { NisoloOrderData } from '../hooks/useNisoloOrders';
import { LineOutcome, reconcileSubmission } from '../utils/submissionOutcome';
import { askCarli, AgentMessage } from '../utils/carliAgent';
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const compact = (value: number) => Math.abs(value) >= 1000 ? `${value < 0 ? '−' : ''}$${Math.round(Math.abs(value) / 1000).toLocaleString('en-US')}K` : currency(value);
const perUnit = (value: number) => `$${value.toFixed(2)}`;
const count = (value: number) => value.toLocaleString('en-US');
const signedPct = (value: number) => `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}%`;
export function UploadAIInsights({
  uploads,
  orders,
  loading,
  error,
  defaultOpen = false
}: {
  uploads: NisoloSubmission[];
  orders: NisoloOrderData | null;
  loading: boolean;
  error: string | null;
  defaultOpen?: boolean;
}) {
  // In the report dialog the single upload starts expanded.
  const [openUpload, setOpenUpload] = useState<string | null>(defaultOpen ? uploads[0]?.id ?? null : null);
  if (loading) {
    return <article className="flex items-center justify-center gap-2 rounded-2xl border border-ink/10 bg-paper px-6 py-12 text-sm text-ink/55" data-id="element-2517" data-mp="3ifzm73knv89d">
        <LoaderCircleIcon className="h-4 w-4 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-2518" data-mp="3nfch8blorln1" />
        Carli is reading the receiving file…
      </article>;
  }
  if (error) {
    return <article className="rounded-2xl border border-ink/10 bg-paper px-6 py-12 text-center text-sm text-ink/55" data-id="element-2519" data-mp="b5ywj5l5x3k">{error}</article>;
  }
  if (uploads.length === 0) {
    return <article className="rounded-2xl border border-ink/10 bg-paper p-8 text-center" aria-labelledby="ai-insights-empty" data-id="element-2520" data-mp="1boqlh2k5gf8u">
        <h2 id="ai-insights-empty" className="text-base font-semibold" data-id="element-2521" data-mp="37rd4yob8isv4">AI insights</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/55" data-id="element-2522" data-mp="33rkv88a3ugo6">Select an inventory submission above to see Carli’s read on what she predicted and what actually happened.</p>
      </article>;
  }
  return <div className="space-y-4" data-id="element-2523" data-mp="3cs0vz9zgr9pc">
      {uploads.map(upload => <InsightReport key={upload.id} upload={upload} orders={orders} open={openUpload === upload.id || defaultOpen && uploads.length === 1} onToggle={() => setOpenUpload(current => current === upload.id ? null : upload.id)} data-id="element-2524" data-mp="2p15b6wtfderl" />)}
    </div>;
}
function InsightReport({
  upload,
  orders,
  open,
  onToggle
}: {
  upload: NisoloSubmission;
  orders: NisoloOrderData | null;
  open: boolean;
  onToggle: () => void;
}) {
  const outcome = useMemo(() => reconcileSubmission(upload, orders), [upload, orders]);
  const weightedMsrp = upload.received > 0 ? upload.msrp / upload.received : 0;
  const coreShare = upload.received > 0 ? Math.round(upload.sizes.filter(size => size.tier === 'Core').reduce((sum, size) => sum + size.units, 0) / upload.received * 100) : 0;
  const headlineTone = outcome.matched ? outcome.variance >= 0 ? 'text-forest' : 'text-[#94452E]' : 'text-ink';
  return <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" data-id="element-2525" data-mp="bsuj8uab5rhr">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-cream/45 sm:px-6" data-id="element-2526" data-mp="zlkwd6ty6gtm">
        <span className="flex min-w-0 items-center gap-3" data-id="element-2527" data-mp="vixwcin4hplc">
          <CarliMark data-id="element-2528" data-mp="vhd3cxggiepl" />
          <span className="min-w-0" data-id="element-2529" data-mp="vl0yg11jv96f">
            <span className="block text-base font-semibold text-ink" data-id="element-2530" data-mp="3ba2jjkmn4017">AI insights · {upload.name}</span>
            <span className="mt-0.5 block text-xs text-ink/50" data-id="element-2531" data-mp="3gagjefe383o5">
              {upload.grade} · {count(upload.received)} units · predicted {compact(upload.estimatedRecovery)}
              {outcome.matched && ` · actual ${compact(outcome.actualSales)} to date`}
            </span>
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-3" data-id="element-2532" data-mp="3lu23o8p1eubl">
          {outcome.matched ? <span className={`hidden items-center gap-1 text-xs font-semibold sm:flex ${headlineTone}`} data-id="element-2533" data-mp="3hsovhryh2f1i">
              {outcome.variance >= 0 ? <TrendingUpIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2534" data-mp="ibquxgwzls0y" /> : <TrendingDownIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2535" data-mp="deid5hfr3q08" />}
              {signedPct(outcome.variancePct)} vs predicted
            </span> : <span className="hidden rounded-full bg-ink/8 px-2.5 py-1 text-[11px] font-semibold text-ink/55 sm:inline" data-id="element-2536" data-mp="4d0z04lwlye6">Awaiting first sales</span>}
          <ChevronDownIcon className={`h-4 w-4 text-ink/45 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2537" data-mp="2u3e3ayv6tsyz" />
        </span>
      </button>

      {open && <div className="border-t border-ink/10 px-5 py-6 sm:px-6" data-id="element-2538" data-mp="14mikp929ln9w">
          <Scoreboard upload={upload} outcome={outcome} data-id="element-2539" data-mp="19j90rwe2kivb" />

          <CarliNarrative upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2540" data-mp="19ho2pwsjqhsp" />

          {outcome.matched && (outcome.overPerformers.length > 0 || outcome.underPerformers.length > 0) && <section className="mt-4 grid gap-3 lg:grid-cols-2" aria-label="Where the model was right and wrong" data-id="element-2541" data-mp="1nbyllxhv3hqu">
              <VarianceList title="Beat the prediction" tone="up" items={outcome.overPerformers} empty="Nothing has cleared above its estimate yet." data-id="element-2542" data-mp="1s9qo1lg4um9a" />
              <VarianceList title="Missed the prediction" tone="down" items={outcome.underPerformers} empty="Nothing has cleared below its estimate yet." data-id="element-2543" data-mp="21aol1js5ltid" />
            </section>}

          <RecoveryFactors upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2544" data-mp="1a7b7hahdh18c" />

          <Composition upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2545" data-mp="15a1hntq2mnjz" />

          <ReportChat upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2546" data-mp="1jaccf7ui4maf" />
        </div>}
    </article>;
}
function Scoreboard({
  upload,
  outcome
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
}) {
  const positive = outcome.variance >= 0;
  return <section aria-label="Predicted versus actual" data-id="element-2547" data-mp="1o5rmdope1y1k">
      <div className="grid gap-3 lg:grid-cols-[1.1fr_1.1fr_0.8fr]" data-id="element-2548" data-mp="22x1njp2ked73">
        <article className="rounded-xl border border-ink/10 bg-cream/45 p-5" data-id="element-2549" data-mp="1xxoyqys4uw1v">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2550" data-mp="1xzrywbn025kb">Predicted recovery</p>
          <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-2551" data-mp="2bv3sugtbzs06">{currency(upload.estimatedRecovery)}</p>
          <p className="mt-1.5 text-xs text-ink/55" data-id="element-2552" data-mp="2qjnge5u5idev">{perUnit(outcome.predictedAur)} per unit across {count(upload.received)} units</p>
        </article>

        <article className={`rounded-xl border p-5 ${outcome.matched ? positive ? 'border-forest/30 bg-forest/6' : 'border-[#BB5E40]/30 bg-[#BB5E40]/6' : 'border-ink/10 bg-cream/45'}`} data-id="element-2553" data-mp="2vigqdoa8bmaa">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2554" data-mp="2ljhl8fooa8s1">Actual recovery to date</p>
          <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${outcome.matched ? positive ? 'text-forest' : 'text-[#94452E]' : 'text-ink/40'}`} data-id="element-2555" data-mp="27rkticqx20w6">
            {outcome.matched ? currency(outcome.actualSales) : '—'}
          </p>
          <p className="mt-1.5 text-xs text-ink/55" data-id="element-2556" data-mp="22tt2rm20cktk">
            {outcome.matched ? `${perUnit(outcome.actualAur)} per unit across ${count(outcome.soldUnits)} units sold` : 'No matched sales recorded against this upload yet'}
          </p>
        </article>

        <article className="rounded-xl border border-ink/10 bg-paper p-5" data-id="element-2557" data-mp="29bnsh964ueg9">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2558" data-mp="299jpqujyzlhk">Variance on sold units</p>
          {outcome.matched ? <>
              <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${positive ? 'text-forest' : 'text-[#94452E]'}`} data-id="element-2559" data-mp="1zcf18o2njdxl">{signedPct(outcome.variancePct)}</p>
              <p className="mt-1.5 text-xs text-ink/55" data-id="element-2560" data-mp="1qdjyjlytglz1">{compact(outcome.variance)} against {currency(outcome.predictedForSold)} predicted for those units</p>
            </> : <>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink/40" data-id="element-2561" data-mp="1gmgwagu5i0y6">—</p>
              <p className="mt-1.5 text-xs text-ink/55" data-id="element-2562" data-mp="1box74lvx03rg">Variance appears once units from this upload sell</p>
            </>}
        </article>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3" data-id="element-2563" data-mp="17odd3r6n1g9j">
        <MiniStat label="Sell-through" value={`${outcome.sellThrough.toFixed(1)}%`} detail={`${count(outcome.soldUnits)} of ${count(upload.received)} units`} data-id="element-2564" data-mp="12os9h3giwjqh" />
        <MiniStat label="Units remaining" value={count(outcome.unsoldUnits)} detail={`${compact(outcome.remainingPredicted)} still forecast`} data-id="element-2565" data-mp="3j9nktbhgf4nj" />
        <MiniStat label="AUR vs forecast" value={outcome.matched ? signedPct(outcome.aurVariancePct) : '—'} detail={outcome.matched ? `${perUnit(outcome.actualAur)} actual vs ${perUnit(outcome.predictedAur)} predicted` : `${perUnit(outcome.predictedAur)} predicted`} tone={outcome.matched ? outcome.aurVariancePct >= 0 ? 'up' : 'down' : undefined} data-id="element-2566" data-mp="3nc0pdwkgswiq" />
      </div>

      <div className="mt-3" data-id="element-2567" data-mp="34joprhl749ca">
        <div className="flex items-center justify-between text-xs text-ink/55" data-id="element-2568" data-mp="39bpxcq7f5bvz">
          <span data-id="element-2569" data-mp="39dstlrz60195">Recovered {compact(outcome.actualSales)}</span>
          <span data-id="element-2570" data-mp="2bvkg6kzi7c4b">Forecast {compact(upload.estimatedRecovery)}</span>
        </div>
        <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-ink/8" role="img" aria-label={`${Math.round(outcome.actualSales / Math.max(1, upload.estimatedRecovery) * 100)}% of forecast recovered`} data-id="element-2571" data-mp="2gunc9fxia805">
          <div className="h-full rounded-full bg-[#BB5E40]" style={{
          width: `${Math.min(100, outcome.actualSales / Math.max(1, upload.estimatedRecovery) * 100)}%`
        }} data-id="element-2572" data-mp="2ogptdux02vlj" />
        </div>
      </div>
    </section>;
}
function MiniStat({
  label,
  value,
  detail,
  tone
}: {
  label: string;
  value: string;
  detail: string;
  tone?: 'up' | 'down';
}) {
  return <article className="rounded-xl border border-ink/10 bg-paper px-4 py-3.5" data-id="element-2573" data-mp="20qbylwn16in9">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2574" data-mp="25rj41p1pb5qr">{label}</p>
      <p className={`mt-1.5 font-display text-xl font-semibold tracking-tight ${tone === 'up' ? 'text-forest' : tone === 'down' ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-2575" data-mp="2ap1ghpexg33i">{value}</p>
      <p className="mt-0.5 text-xs text-ink/50" data-id="element-2576" data-mp="1i0nk1e3hleu2">{detail}</p>
    </article>;
}
function VarianceList({
  title,
  tone,
  items,
  empty
}: {
  title: string;
  tone: 'up' | 'down';
  items: LineOutcome[];
  empty: string;
}) {
  const up = tone === 'up';
  return <article className="rounded-xl border border-ink/10 bg-paper p-5" data-id="element-2577" data-mp="1fewgh2dpbf8s">
      <div className="flex items-center gap-2" data-id="element-2578" data-mp="1aixol4t5zf3m">
        {up ? <TrendingUpIcon className="h-4 w-4 text-forest" aria-hidden="true" data-id="element-2579" data-mp="15ijoqa1pvbgo" /> : <TrendingDownIcon className="h-4 w-4 text-[#94452E]" aria-hidden="true" data-id="element-2580" data-mp="15kw3yfg3b7q1" />}
        <h4 className="text-sm font-semibold" data-id="element-2581" data-mp="10m2ssto9zst1">{title}</h4>
      </div>
      {items.length === 0 ? <p className="mt-3 text-xs text-ink/50" data-id="element-2582" data-mp="wkhkllmos2p6">{empty}</p> : <ul className="mt-3 space-y-2.5" data-id="element-2583" data-mp="rqwjvltpkuma">
          {items.map(item => <li key={item.name} className="flex items-center justify-between gap-3 border-b border-ink/8 pb-2.5 last:border-0 last:pb-0" data-id="element-2584" data-mp="mrsc8zmfn4y2">
              <span className="min-w-0" data-id="element-2585" data-mp="hvblryrrgeht">
                <span className="block truncate text-xs font-medium text-ink" data-id="element-2586" data-mp="cu5pjqyw693j">{item.name}</span>
                <span className="text-[11px] text-ink/50" data-id="element-2587" data-mp="8t2f5jzm559p">
                  {count(item.soldUnits)} of {count(item.receivedUnits)} sold · {perUnit(item.actualAur)} actual vs {perUnit(item.predict)} predicted
                </span>
              </span>
              <span className={`shrink-0 text-xs font-semibold tabular-nums ${up ? 'text-forest' : 'text-[#94452E]'}`} data-id="element-2588" data-mp="38cty2ozri2k">
                {item.variance >= 0 ? '+' : '−'}{currency(Math.abs(item.variance))}
              </span>
            </li>)}
        </ul>}
    </article>;
}
function CarliNarrative({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState('');
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSummary('');
    setFailed('');
    askCarli({
      messages: [{
        role: 'user',
        content: 'Give me your read on this upload: what you predicted, what actually happened, why, and what to do next.'
      }],
      context: buildContext(upload, outcome, weightedMsrp, coreShare, ['Reply with exactly three lines, each starting with the label and a colon, and nothing else:', 'CALL: two sentences on what the model predicted for this lot and the reasoning behind that number.', 'RESULT: two sentences on what actually happened against that prediction, citing the specific styles, sizes, or price points responsible.', 'NEXT: two sentences on the single highest-value action to take now, stated concretely with the units or dollars involved.', 'Write the full sentences on the same line as the label. Do not add preamble, bullet points, markdown, or any other headings.']),
      maxTokens: 700
    }).then(reply => {
      if (!cancelled) setSummary(reply);
    }).catch((caught: Error) => {
      if (!cancelled) setFailed(caught.message);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [upload, outcome, weightedMsrp, coreShare]);
  const sections = useMemo(() => {
    if (!summary) return [];
    const order = ['CALL', 'RESULT', 'NEXT'];
    const positions = order.map(label => ({
      label,
      index: summary.search(new RegExp(`\\b${label}\\s*:`, 'i'))
    })).filter(entry => entry.index >= 0).sort((a, b) => a.index - b.index);
    return positions.map((entry, position) => {
      const next = positions[position + 1];
      const slice = summary.slice(entry.index, next ? next.index : undefined);
      return {
        label: entry.label,
        text: slice.replace(new RegExp(`^\\s*${entry.label}\\s*:\\s*`, 'i'), '').replace(/[*_#]/g, '').trim()
      };
    }).filter(section => section.text.length > 0);
  }, [summary]);
  const labels: Record<string, string> = {
    CALL: 'The call',
    RESULT: 'The result',
    NEXT: 'What to do next'
  };
  return <section className="mt-4 rounded-xl border border-[#BB5E40]/25 bg-[#BB5E40]/5 p-5" aria-label="Carli’s read" data-id="element-2589" data-mp="1sm18p4646xk8">
      <div className="flex items-center gap-2.5" data-id="element-2590" data-mp="3d5wjoemjhsg3">
        <CarliMark data-id="element-2591" data-mp="3d2h7pko5wziw" />
        <span data-id="element-2592" data-mp="3858wy55cfxro">
          <span className="block text-sm font-semibold" data-id="element-2593" data-mp="385h7vrhwot08">Carli’s read</span>
          <span className="block text-xs text-ink/50" data-id="element-2594" data-mp="343vpjqmlrmin">{upload.name} · {upload.grade}</span>
        </span>
      </div>

      {loading && <p className="mt-3 flex items-center gap-2 text-xs text-ink/55" data-id="element-2595" data-mp="2z6dpkpqkchms">
          <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-2596" data-mp="3vvuw16xde7pj" />
          Comparing the forecast against actual sales…
        </p>}

      {failed && !loading && <p className="mt-3 text-xs leading-relaxed text-ink/60" data-id="element-2597" data-mp="3n2hp312h15kg">{failed}</p>}

      {sections.length > 0 && <dl className="mt-3 grid gap-4 lg:grid-cols-3" data-id="element-2598" data-mp="3i4pxy99nhnhb">
          {sections.map(section => <div key={section.label} data-id="element-2599" data-mp="je6abchzlypf">
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2600" data-mp="jf935pyveb7z">{labels[section.label.toUpperCase()] ?? section.label}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-ink/75" data-id="element-2601" data-mp="xj6m564b0dds">{section.text}</dd>
            </div>)}
        </dl>}

      {!loading && !failed && sections.length === 0 && summary && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink/75" data-id="element-2602" data-mp="oq3fphs2t0sn">{summary}</p>}
    </section>;
}
type Strength = 'Strong' | 'Moderate' | 'Limited';
const strengthTone: Record<Strength, string> = {
  Strong: 'bg-forest/12 text-forest',
  Moderate: 'bg-[#C9A04E]/20 text-[#8B6A20]',
  Limited: 'bg-[#BB5E40]/14 text-[#94452E]'
};
function RecoveryFactors({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const recoveryRate = upload.msrp > 0 ? Math.round(upload.estimatedRecovery / upload.msrp * 100) : 0;
  const topCategory = upload.categories[0];
  const categoryShare = upload.received > 0 && topCategory ? Math.round(topCategory.units / upload.received * 100) : 0;
  const deepLines = upload.lines.filter(line => line.units >= 100).length;
  const premiumUnits = upload.priceBands.filter(band => band.label === '$150 – $199' || band.label === '$200+').reduce((sum, band) => sum + band.units, 0);
  const premiumShare = upload.received > 0 ? Math.round(premiumUnits / upload.received * 100) : 0;
  const unsized = upload.sizes.find(size => size.label === 'UNSIZED' || size.label === 'Unsized')?.units ?? 0;
  const marketing = outcome.predictedAur * upload.received * 0.1;
  const dataGaps = upload.missingMsrp + upload.missingPredict + upload.missingUpc;
  const factors: Array<{
    title: string;
    strength: Strength;
    headline: string;
    points: string[];
    takeaway: string;
  }> = [{
    title: 'Pricing position',
    strength: outcome.matched ? outcome.aurVariancePct >= 0 ? 'Strong' : 'Moderate' : 'Moderate',
    headline: outcome.matched ? `Units are clearing at ${perUnit(outcome.actualAur)}, ${signedPct(outcome.aurVariancePct)} against the ${perUnit(outcome.predictedAur)} forecast.` : `The lot is modeled to clear at ${perUnit(outcome.predictedAur)}, ${recoveryRate}% of submitted MSRP.`,
    points: [`${perUnit(weightedMsrp)} weighted MSRP per unit`, `${perUnit(weightedMsrp * 0.7)} A-grade promotional price at ~30% off`, outcome.matched ? `${perUnit(outcome.actualAur)} realized on ${count(outcome.soldUnits)} units` : `${perUnit(outcome.predictedAur)} predicted recovery per unit`],
    takeaway: outcome.matched && outcome.aurVariancePct >= 0 ? 'Prices are holding above plan — hold the ladder rather than discounting.' : 'Price sits well below first-quality, which protects the brand but caps recovery.'
  }, {
    title: 'Product mix',
    strength: categoryShare >= 55 ? 'Limited' : categoryShare >= 35 ? 'Moderate' : 'Strong',
    headline: topCategory ? `${topCategory.label} carries ${categoryShare}% of units, setting the pace for the whole lot.` : 'Category mix is spread evenly across the assortment.',
    points: [`${count(upload.lineItems)} variant lines across ${count(upload.categories.length)} categories`, `${count(deepLines)} lines carry 100+ units`, upload.categories.slice(1, 3).map(item => `${item.label} ${count(item.units)}`).join(' · ') || 'No secondary category depth'],
    takeaway: categoryShare >= 55 ? 'Concentration means one category decides the outcome — merchandise it deliberately.' : 'A balanced mix gives multiple paths to recovery if one category stalls.'
  }, {
    title: 'Size distribution',
    strength: coreShare >= 55 ? 'Strong' : coreShare >= 35 ? 'Moderate' : 'Limited',
    headline: `${coreShare}% of units sit in core-demand sizes, which drive the fastest sell-through.`,
    points: [upload.sizes.filter(size => size.tier === 'Core').slice(0, 3).map(size => `${size.label} (${count(size.units)})`).join(' · ') || 'No core sizes identified', `${count(upload.sizes.filter(size => size.tier === 'Tail').reduce((sum, size) => sum + size.units, 0))} units in tail sizes`, unsized > 0 ? `${count(unsized)} units without a readable size` : 'Every unit carries a readable size'],
    takeaway: coreShare >= 50 ? 'The size curve favors liquidity — lead with core runs.' : 'Tail sizes need more time or markdown to clear.'
  }, {
    title: 'Price architecture',
    strength: premiumShare >= 45 ? 'Strong' : premiumShare >= 20 ? 'Moderate' : 'Limited',
    headline: `${premiumShare}% of units carry MSRP of $150 or higher, where recovery per unit is strongest.`,
    points: upload.priceBands.slice(0, 3).map(band => `${band.label}: ${count(band.units)} units`),
    takeaway: premiumShare >= 40 ? 'Higher retail price points give room to discount without eroding recovery.' : 'A lower-priced lot means volume, not price, has to carry recovery.'
  }, {
    title: 'Promotion potential',
    strength: marketing >= 20000 ? 'Strong' : marketing >= 5000 ? 'Moderate' : 'Limited',
    headline: `Projected recovery supports roughly ${compact(marketing)} of marketing at the standard 10% allocation.`,
    points: [`${currency(upload.estimatedRecovery)} projected gross recovery`, `${compact(marketing)} available for channel and content push`, outcome.matched ? `${count(outcome.unsoldUnits)} units still to place` : `${count(upload.received)} units awaiting first placement`],
    takeaway: 'Spend is best concentrated on the categories already converting.'
  }, {
    title: 'Data integrity',
    strength: dataGaps === 0 ? 'Strong' : dataGaps < upload.lineItems * 0.1 ? 'Moderate' : 'Limited',
    headline: dataGaps === 0 ? 'Every line carries MSRP, barcode, and a per-unit estimate.' : `${count(dataGaps)} field gaps across ${count(upload.lineItems)} lines widen the projection range.`,
    points: [`${count(upload.lineItems - upload.missingUpc)} of ${count(upload.lineItems)} lines have a barcode`, `${count(upload.lineItems - upload.missingMsrp)} of ${count(upload.lineItems)} lines have MSRP`, `${count(upload.shortShipped)} invoiced units never arrived`],
    takeaway: dataGaps === 0 ? 'Clean identifiers let us match sales back to this upload precisely.' : 'Gaps mean some sales cannot be attributed back to this lot.'
  }];
  return <section className="mt-4" aria-label="Recovery factors" data-id="element-2603" data-mp="3uxwtxjgmk273">
      <h3 className="text-sm font-semibold" data-id="element-2604" data-mp="3uzhqtfqevx84">What is driving this lot</h3>
      <div className="mt-3 grid gap-3 lg:grid-cols-3" data-id="element-2605" data-mp="3q65cbyfg56t3">
        {factors.map(factor => <article key={factor.title} className="flex flex-col rounded-xl border border-ink/10 bg-paper p-4" data-id="element-2606" data-mp="32gu7a4r2cq95">
            <div className="flex items-start justify-between gap-2" data-id="element-2607" data-mp="2xhhj9goghd4z">
              <h4 className="text-sm font-semibold" data-id="element-2608" data-mp="1czynwcqbggj0">{factor.title}</h4>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${strengthTone[factor.strength]}`} data-id="element-2609" data-mp="1cynlag8lxeth">{factor.strength}</span>
            </div>
            <p className="mt-2 text-xs font-semibold leading-relaxed text-ink/80" data-id="element-2610" data-mp="141d9mtrvnno1">{factor.headline}</p>
            <ul className="mt-2.5 space-y-1.5" data-id="element-2611" data-mp="181x3noh5mb5y">
              {factor.points.filter(Boolean).map(point => <li key={point} className="flex gap-2 text-xs leading-relaxed text-ink/60" data-id="element-2612" data-mp="1vrrk0g0omhuy">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#BB5E40]" aria-hidden="true" data-id="element-2613" data-mp="20p9sk5asev2f" />
                  {point}
                </li>)}
            </ul>
            <p className="mt-auto border-t border-ink/10 pt-2.5 text-xs leading-relaxed text-ink/55" style={{
          marginTop: '0.875rem'
        }} data-id="element-2614" data-mp="1ro1yjzfb0ge5">{factor.takeaway}</p>
          </article>)}
      </div>
    </section>;
}
function Composition({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [open, setOpen] = useState(false);
  const recoveryRate = upload.msrp > 0 ? Math.round(upload.estimatedRecovery / upload.msrp * 100) : 0;
  return <section className="mt-4 overflow-hidden rounded-xl border border-ink/10 bg-paper" aria-label="Assumptions and composition" data-id="element-2615" data-mp="hszprdfj857r">
      <button type="button" onClick={() => setOpen(current => !current)} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-cream/45" data-id="element-2616" data-mp="1ehfi1ha10sok">
        <span data-id="element-2617" data-mp="10ixwnq7lfs9t">
          <span className="block text-sm font-semibold text-ink" data-id="element-2618" data-mp="10jh58gbct4b6">How the forecast was built</span>
          <span className="mt-0.5 block text-xs text-ink/50" data-id="element-2619" data-mp="3q8suyw4exu9i">
            {perUnit(weightedMsrp)} weighted MSRP · {recoveryRate}% modeled recovery · {coreShare}% core sizes · {count(upload.lineItems)} variant lines
          </span>
        </span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2620" data-mp="20syz481vbzhz" />
      </button>

      {open && <div className="border-t border-ink/10 px-5 py-5" data-id="element-2621" data-mp="29r0x0eqb2c11">
          <ol className="grid gap-3 lg:grid-cols-4" data-id="element-2622" data-mp="2eial3aw7h7at">
            <WaterfallStep label="Weighted MSRP" value={perUnit(weightedMsrp)} detail="Retail value per unit across the lot." data-id="element-2623" data-mp="2ejtymtqh948w" />
            <WaterfallStep label="A-grade promo price" value={perUnit(weightedMsrp * 0.7)} detail="Nisolo was running ~30% off new product." data-id="element-2624" data-mp="2ogqa98boa6ho" />
            <WaterfallStep label="Predicted recovery" value={perUnit(outcome.predictedAur)} detail={`${recoveryRate}% of MSRP after condition and demand.`} data-id="element-2625" data-mp="2tguv1ajm5ndp" />
            <WaterfallStep label="Actual recovery" value={outcome.matched ? perUnit(outcome.actualAur) : '—'} detail={outcome.matched ? `${signedPct(outcome.aurVariancePct)} against the forecast.` : 'Pending first matched sales.'} highlight data-id="element-2626" data-mp="z1s8nlpv2lf0" />
          </ol>

          <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-2627" data-mp="21fztzfgbzrc8">
            <BreakdownStat label="Variant lines" value={count(upload.lineItems)} detail={`${count(upload.received)} units received`} data-id="element-2628" data-mp="21hjan75wg4ej" />
            <BreakdownStat label="Units per line" value={upload.lineItems > 0 ? (upload.received / upload.lineItems).toFixed(1) : '—'} detail={`${count(upload.lines.filter(line => line.units >= 100).length)} lines carry 100+ units`} data-id="element-2629" data-mp="2bcuoln9hh7l8" />
            <BreakdownStat label="Modeled recovery" value={`${recoveryRate}%`} detail={`of ${currency(upload.msrp)} submitted MSRP`} data-id="element-2630" data-mp="2kdj46meizg08" />
          </div>

          <div className="mt-3 grid gap-3 lg:grid-cols-3" data-id="element-2631" data-mp="2u43yvyr2w0dv">
            <Distribution title="Sizes" items={upload.sizes.slice(0, 8)} total={upload.received} tiered data-id="element-2632" data-mp="2u4nhlhojyssy" />
            <Distribution title="Categories" items={upload.categories.slice(0, 8)} total={upload.received} data-id="element-2633" data-mp="381kbxt3vqsel" />
            <Distribution title="MSRP bands" items={upload.priceBands} total={upload.received} data-id="element-2634" data-mp="332phftwjvfen" />
          </div>

          <div className="mt-4 grid gap-4 border-t border-ink/10 pt-4 lg:grid-cols-2" data-id="element-2635" data-mp="3rp6nrqwgn5ek">
            <div data-id="element-2636" data-mp="3rn2jh7ung4dr">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2637" data-mp="3dpxgjgq8flmq">Confidence: medium — what we knew</p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/65" data-id="element-2638" data-mp="3hq7lcq2ag600">
                Unit counts, MSRP, size mix, and the {upload.grade} designation were known. {count(upload.lineItems - upload.missingPredict)} of {count(upload.lineItems)} lines carried a per-unit estimate, and {count(upload.shortShipped)} invoiced units never arrived.
              </p>
            </div>
            <div data-id="element-2639" data-mp="2z084gqglpx7o">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2640" data-mp="2z1sxucr6ja3y">What we had to estimate</p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/65" data-id="element-2641" data-mp="2l1i6l8lyjti9">
                Demand by style, liquidity by size, and how consistently the {upload.grade} condition would land with customers — the network had no prior Nisolo sell-through to calibrate against.
              </p>
            </div>
          </div>
        </div>}
    </section>;
}
function WaterfallStep({
  label,
  value,
  detail,
  highlight
}: {
  label: string;
  value: string;
  detail: string;
  highlight?: boolean;
}) {
  return <li className={`rounded-xl border p-4 ${highlight ? 'border-[#BB5E40] bg-[#BB5E40]/6' : 'border-ink/10 bg-cream/40'}`} data-id="element-2642" data-mp="2ri1uh1g76pgh">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2643" data-mp="2ml1k4ecbf0jk">{label}</p>
      <p className={`mt-1.5 font-display text-xl font-semibold tracking-tight ${highlight ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-2644" data-mp="2hqozw230ll6r">{value}</p>
      <p className="mt-1.5 text-xs leading-relaxed text-ink/55" data-id="element-2645" data-mp="27v3t7qn03wqo">{detail}</p>
    </li>;
}
function BreakdownStat({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-xl border border-ink/10 bg-cream/40 px-4 py-3.5" data-id="element-2646" data-mp="1yte44rgld9w2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2647" data-mp="ebvdu1xb73gy">{label}</p>
      <p className="mt-1.5 font-display text-xl font-semibold tracking-tight text-ink" data-id="element-2648" data-mp="jag87kpvqsxh">{value}</p>
      <p className="mt-0.5 text-xs text-ink/50" data-id="element-2649" data-mp="ncjl8ds765kv">{detail}</p>
    </article>;
}
function Distribution({
  title,
  items,
  total,
  tiered
}: {
  title: string;
  items: Array<{
    label: string;
    units: number;
    tier?: SizeTier;
  }>;
  total: number;
  tiered?: boolean;
}) {
  const max = Math.max(...items.map(item => item.units), 1);
  const tone: Record<SizeTier, string> = {
    Core: 'bg-[#BB5E40]',
    Adjacent: 'bg-[#C9A04E]',
    Tail: 'bg-ink/25'
  };
  return <article data-id="element-2650" data-mp="4l0jea9wsxkf">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2651" data-mp="4khb7la29njl">{title}</p>
      <ul className="mt-2.5 space-y-1.5" data-id="element-2652" data-mp="9e3dcjilm915">
        {items.map(item => <li key={item.label} className="flex items-center gap-2.5" data-id="element-2653" data-mp="381he87p3v80h">
            <span className="w-20 shrink-0 truncate text-xs text-ink/65" data-id="element-2654" data-mp="3d1c5wdgpg1ml">{item.label}</span>
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/8" data-id="element-2655" data-mp="2mrokx0yjwn76">
              <span className={`block h-full rounded-full ${tiered ? tone[item.tier ?? 'Tail'] : 'bg-[#BB5E40]'}`} style={{
            width: `${Math.max(2, item.units / max * 100)}%`
          }} data-id="element-2656" data-mp="2mpvg7r7i0d1t" />
            </span>
            <span className="w-9 shrink-0 text-right text-xs tabular-nums text-ink/55" data-id="element-2657" data-mp="2dtco2h73ct6a">{total > 0 ? Math.round(item.units / total * 100) : 0}%</span>
          </li>)}
      </ul>
    </article>;
}
function buildContext(upload: NisoloSubmission, outcome: ReturnType<typeof reconcileSubmission>, weightedMsrp: number, coreShare: number, instructions: string[]) {
  return [`Inventory submission: "${upload.name}" (${upload.grade}), uploaded ${upload.arrived}, brand Nisolo, sold through the Manymoons recovery network.`, `Received ${upload.received} units across ${upload.lineItems} variant lines. ${upload.shortShipped} invoiced units never arrived.`, `Submitted MSRP value ${currency(upload.msrp)} (${perUnit(weightedMsrp)} weighted per unit). Nisolo A-grade was promoting at ~30% off, so first-quality price sat near ${perUnit(weightedMsrp * 0.7)}.`, `FORECAST: ${currency(upload.estimatedRecovery)} total, ${perUnit(outcome.predictedAur)} per unit received.`, outcome.matched ? `ACTUAL SO FAR: ${outcome.soldUnits} units sold for ${currency(outcome.actualSales)} (${perUnit(outcome.actualAur)} AUR). Those same units were forecast at ${currency(outcome.predictedForSold)}, so variance is ${currency(outcome.variance)} (${signedPct(outcome.variancePct)}). AUR variance ${signedPct(outcome.aurVariancePct)}. Sell-through ${outcome.sellThrough.toFixed(1)}%. ${outcome.unsoldUnits} units remain, carrying ${currency(outcome.remainingPredicted)} of remaining forecast.` : 'ACTUAL SO FAR: no sales have been matched to this upload yet, so there is no realized variance.', outcome.overPerformers.length > 0 ? `Beat forecast: ${outcome.overPerformers.map(item => `${item.name} (${item.soldUnits} of ${item.receivedUnits} sold, ${perUnit(item.actualAur)} vs ${perUnit(item.predict)} predicted)`).join('; ')}.` : '', outcome.underPerformers.length > 0 ? `Missed forecast: ${outcome.underPerformers.map(item => `${item.name} (${item.soldUnits} of ${item.receivedUnits} sold, ${perUnit(item.actualAur)} vs ${perUnit(item.predict)} predicted)`).join('; ')}.` : '', `Composition: ${coreShare}% of units in core-demand sizes. Categories: ${upload.categories.slice(0, 6).map(item => `${item.label} (${item.units})`).join(', ')}. MSRP bands: ${upload.priceBands.map(item => `${item.label} (${item.units})`).join(', ')}.`, '', ...instructions].filter(Boolean).join('\n');
}
function ReportChat({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [messages, setMessages] = useState<Array<{
    id: number;
    role: 'user' | 'assistant';
    text: string;
  }>>([]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const suggestions = outcome.matched ? ['Why did the forecast miss?', 'What should we reprice?', 'How should we clear what’s left?'] : ['What should launch first?', 'How should we price this?', 'What are the biggest risks?'];
  const send = async (question: string) => {
    if (!question.trim() || thinking) return;
    const history: AgentMessage[] = [...messages.map(message => ({
      role: message.role,
      content: message.text
    })), {
      role: 'user',
      content: question
    }];
    setMessages(current => [...current, {
      id: Date.now(),
      role: 'user',
      text: question
    }]);
    setDraft('');
    setThinking(true);
    try {
      const reply = await askCarli({
        messages: history,
        context: buildContext(upload, outcome, weightedMsrp, coreShare, ['Answer only from this submission data. Be specific and commercial. Two short paragraphs maximum.']),
        maxTokens: 600
      });
      setMessages(current => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: reply
      }]);
    } catch (caught) {
      setMessages(current => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: (caught as Error).message
      }]);
    } finally {
      setThinking(false);
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void send(draft.trim());
  };
  return <section className="mt-4 rounded-xl border border-[#BB5E40]/30 bg-[#BB5E40]/5 p-4 sm:p-5" aria-label="Ask Carli about this report" data-id="element-2658" data-mp="wnpm0fi6pl5x">
      <div className="flex items-center gap-2.5" data-id="element-2659" data-mp="mrw92v0jzpnm">
        <CarliMark data-id="element-2660" data-mp="mre6zhr7mh7s" />
        <span data-id="element-2661" data-mp="mu0czcygconw">
          <span className="block text-sm font-semibold" data-id="element-2662" data-mp="rqh1i89kd51i">Ask Carli about this upload</span>
          <span className="block text-xs text-ink/55" data-id="element-2663" data-mp="37hv5q8c5ecax">She answers from this submission’s units, pricing, and realized sales.</span>
        </span>
      </div>

      {messages.length > 0 && <ul className="mt-4 max-h-56 space-y-2.5 overflow-y-auto pr-1" style={{
      scrollbarGutter: 'stable'
    }} aria-live="polite" data-id="element-2664" data-mp="3celleuk1j5vt">
          {messages.map(message => <li key={message.id} className={message.role === 'user' ? 'flex justify-end' : 'flex gap-2.5'} data-id="element-2665" data-mp="2zxua3uvxps89">
              {message.role === 'assistant' && <CarliMark compact data-id="element-2666" data-mp="38z0s68e8fb3m" />}
              <p className={`max-w-2xl whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${message.role === 'user' ? 'bg-[#BB5E40] text-cream' : 'bg-paper text-ink/80'}`} data-id="element-2667" data-mp="3903myreodxot">{message.text}</p>
            </li>)}
        </ul>}

      {thinking && <p className="mt-3 flex items-center gap-2 text-xs text-[#94452E]" data-id="element-2668" data-mp="2uzsvbm5jkf2l">
          <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-2669" data-mp="2q8j6uovqbhsa" />
          Carli is reviewing this upload…
        </p>}

      <form onSubmit={submit} className="mt-3 flex items-center gap-2 rounded-xl border border-[#BB5E40]/30 bg-paper p-1.5 pl-3.5 focus-within:border-[#BB5E40] focus-within:ring-2 focus-within:ring-[#BB5E40]/15" data-id="element-2670" data-mp="22hngzxau2wno">
        <label htmlFor={`report-chat-${upload.id}`} className="sr-only" data-id="element-2671" data-mp="1g7fq6xfi5vf">Ask Carli about {upload.name}</label>
        <input id={`report-chat-${upload.id}`} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Ask Carli about this upload…" className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink/40" data-id="element-2672" data-mp="6dzb5axa8a4d" />
        <button type="submit" disabled={!draft.trim() || thinking} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] text-cream transition-colors hover:bg-[#94452E] disabled:cursor-not-allowed disabled:bg-ink/15" aria-label="Send question" data-id="element-2673" data-mp="3epd77t3inpmr">
          {thinking ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-2674" data-mp="13vqzz1gfouoe" /> : <SendHorizontalIcon className="h-4 w-4" data-id="element-2675" data-mp="13xaccgyyylkw" />}
        </button>
      </form>

      {messages.length === 0 && <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-2676" data-mp="2seu0kakyfvxg">
          {suggestions.map(item => <button key={item} type="button" onClick={() => void send(item)} className="rounded-full border border-[#BB5E40]/35 bg-paper px-3 py-1.5 text-xs font-medium text-[#94452E] transition-colors hover:bg-[#BB5E40] hover:text-cream" data-id="element-2677" data-mp="2od8h26dx0jea">
              {item}
            </button>)}
        </div>}
    </section>;
}
function CarliMark({
  compact = false
}: {
  compact?: boolean;
}) {
  return <span aria-hidden="true" className={`flex shrink-0 items-center justify-center rounded-lg bg-[#BB5E40]/12 ${compact ? 'h-7 w-7' : 'h-9 w-9'}`} data-id="element-2678" data-mp="24rdj62z89lla">
      <span className="bg-[#BB5E40]" style={{
      width: compact ? '0.875rem' : '1.125rem',
      height: compact ? '0.875rem' : '1.125rem',
      WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
      maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-2679" data-mp="1vpcn39fqd5l7" />
    </span>;
}